import type { JournalEntry, LocalRecord, SavedExport } from './types';

const DB = 'digevidence-local-v1';
const VERSION = 2;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB, VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      for (const [name, keyPath] of [['records','id'],['keys','id'],['journal',['recordId','seq']],['queue','recordId'],['exports','recordId']] as const) {
        if (!db.objectStoreNames.contains(name)) db.createObjectStore(name, {keyPath:typeof keyPath === 'string' ? keyPath : [...keyPath]});
      }
    };
    request.onsuccess = () => { request.result.onversionchange = () => request.result.close(); resolve(request.result); };
    request.onerror = () => reject(request.error ?? new Error('Не удалось открыть локальное хранилище'));
  });
}

async function request<T>(store: string, mode: IDBTransactionMode, action: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, mode);
    const req = action(tx.objectStore(store));
    let result: T;
    req.onsuccess = () => { result = req.result; };
    req.onerror = () => reject(req.error ?? new Error('Ошибка локального хранилища'));
    tx.onabort = () => { db.close(); reject(tx.error ?? new Error('Локальная запись отменена')); };
    tx.oncomplete = () => { db.close(); resolve(result); };
  });
}

export const saveRecord = (value: LocalRecord) => request('records', 'readwrite', s => s.put(value));
export const getRecord = (id: string) => request<LocalRecord | undefined>('records', 'readonly', s => s.get(id));
export const listRecords = () => request<LocalRecord[]>('records', 'readonly', s => s.getAll());
export const saveKey = (id: string, key: CryptoKey) => request('keys', 'readwrite', s => s.put({ id, key }));
export const getKey = async (id: string) => (await request<{ id: string; key: CryptoKey } | undefined>('keys', 'readonly', s => s.get(id)))?.key;
export const putQueue = (recordId: string, body: unknown) => request('queue', 'readwrite', s => s.put({ recordId, body, savedAt: new Date().toISOString() }));
export const listQueue = () => request<Array<{ recordId: string; body: unknown }>>('queue', 'readonly', s => s.getAll());
export const deleteQueue = (recordId: string) => request('queue', 'readwrite', s => s.delete(recordId));
export const getQueuedRecord = (recordId: string) => request<{recordId:string;body:unknown}|undefined>('queue','readonly',s=>s.get(recordId));
export const appendJournal = (entry: JournalEntry) => request('journal', 'readwrite', s => s.add(entry));
export const readJournal = (recordId: string) => request<JournalEntry[]>('journal', 'readonly', s => s.getAll(IDBKeyRange.bound([recordId, 0], [recordId, Number.MAX_SAFE_INTEGER])));
export const getSavedExport = (recordId: string) => request<SavedExport | undefined>('exports','readonly',s=>s.get(recordId));
export const listSavedExports = () => request<SavedExport[]>('exports','readonly',s=>s.getAll());
export const saveExport = (job: SavedExport) => request('exports','readwrite',s=>s.put(job));
export const deleteExport = (recordId: string) => request('exports','readwrite',s=>s.delete(recordId));

const running = new Map<string,Promise<unknown>>();
export async function withRecordLock<T>(key: string, action: () => Promise<T>): Promise<T> {
  if (typeof navigator !== 'undefined' && navigator.locks) return navigator.locks.request(`digevidence:${key}`,action);
  const previous = running.get(key) ?? Promise.resolve();
  const next = previous.catch(()=>undefined).then(action);
  running.set(key,next);
  try {return await next;} finally {if (running.get(key) === next) running.delete(key);}
}

// Hashing runs outside the transaction. The final compare-and-add is atomic across tabs.
export async function appendJournalSafely(recordId: string, create: (chain: JournalEntry[]) => Promise<JournalEntry>, record?: LocalRecord): Promise<JournalEntry> {
  for (let attempt=0;attempt<32;attempt++) {
    const entry = await create(await readJournal(recordId));
    const db = await openDb();
    const committed = await new Promise<boolean>((resolve,reject)=>{
      const tx = db.transaction(record ? ['journal','records'] : ['journal'],'readwrite');
      let conflict = false;
      const store = tx.objectStore('journal');
      const tail = store.openCursor(IDBKeyRange.bound([recordId,0],[recordId,Number.MAX_SAFE_INTEGER]),'prev');
      tail.onsuccess = () => {
        const last = tail.result?.value as JournalEntry | undefined;
        if (entry.recordId !== recordId || entry.seq !== (last?.seq ?? 0)+1 || entry.previousHash !== (last?.hash ?? '0'.repeat(64))) {conflict=true;tx.abort();return;}
        store.add(entry);
        if (record) tx.objectStore('records').put(record);
      };
      tx.oncomplete = () => {db.close();resolve(true);};
      tx.onabort = () => {db.close();if(conflict) resolve(false);else reject(tx.error ?? new Error('Не удалось записать событие'));};
    });
    if (committed) return entry;
  }
  throw new Error('Журнал занят другой вкладкой. Повторите действие.');
}

export async function restoreRecord(record: LocalRecord, journal: JournalEntry[]): Promise<'restored'|'exists'> {
  const db = await openDb();
  return new Promise((resolve,reject)=>{
    const tx = db.transaction(['records','journal'],'readwrite');
    let result: 'restored'|'exists' = 'restored';
    let error: Error | undefined;
    const records = tx.objectStore('records');
    const existing = records.get(record.id);
    existing.onsuccess = () => {
      const saved = existing.result as LocalRecord | undefined;
      if (saved) {
        if (saved.manifestSha256 !== record.manifestSha256 || saved.fileSha256 !== record.fileSha256) {error=new Error('Этот идентификатор уже связан с другим манифестом. Запись не изменена.');tx.abort();}
        else result='exists';
        return;
      }
      records.add(record);
      for (const entry of journal) tx.objectStore('journal').add(entry);
    };
    tx.oncomplete = () => {db.close();resolve(result);};
    tx.onabort = () => {db.close();reject(error ?? tx.error ?? new Error('Не удалось восстановить пакет'));};
  });
}

export async function localUsage(): Promise<{ usage?: number; quota?: number }> {
  try { return navigator.storage?.estimate ? await navigator.storage.estimate() : {}; }
  catch { return {}; }
}

export function storageWriteError(error: unknown): string {
  if (error instanceof DOMException && error.name === 'QuotaExceededError') {
    return 'В браузере недостаточно места. Черновик не сохранён. Освободите место и повторите сохранение файла.';
  }
  return error instanceof Error ? `Не удалось сохранить черновик: ${error.message}` : 'Не удалось сохранить черновик.';
}

export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  return navigator.storage.persist();
}

export async function saveDraft(record: LocalRecord, key: CryptoKey, journal: JournalEntry, body: unknown): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(['records', 'keys', 'journal', 'queue'], 'readwrite');
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onabort = () => { db.close(); reject(tx.error ?? new Error('Локальная запись отменена')); };
    try {
      tx.objectStore('records').add(record);
      tx.objectStore('keys').add({ id: record.keyId, key });
      tx.objectStore('journal').add(journal);
      tx.objectStore('queue').add({ recordId: record.id, body, savedAt: new Date().toISOString() });
    } catch (error) { tx.abort(); reject(error); }
  });
}
